'use client'

import { useId } from 'react'
import { Input as InputMain } from "@/components/ui/input"
import { Button } from "./Button"
import { Label } from "@/components/ui/label"
import { useT } from '@/components/i18n/LocaleProvider'
import { InputGroup, InputGroupAddon, InputGroupInput, } from "@/components/ui/input-group"

/**
 * Input component for user input.
 *
 * @param {Object} props - The input component props.
 * @param {string} [props.type='text'] - The type of input field.
 * @param {string} [props.description='This is your public display name.'] - The description of the input field.
 * @param {string} [props.placeholder] - Defaults to the translated "type here" text. - The placeholder text for the input field.
 * @param {string} props.label - The label for the input field.
 * @param {ReactNode} props.leftIcon - The icon to display on the left side of the input field.
 * @param {ReactNode} props.rightIcon - The icon to display on the right side of the input field.
 * @param {function} props.leftAction - The action to perform when the left icon is clicked.
 * @param {function} props.rightAction - The action to perform when the right icon is clicked.
 * @param {boolean} [props.hasButton=false] - Indicates whether the input field has a button.
 * @param {string} [props.buttonText] - Defaults to the translated "save" text. - The text to display on the button.
 * @param {string} [props.buttonVariant='default'] - The variant of the button.
 * @param {function} props.buttonAction - The action to perform when the button is clicked.
 * @param {ReactNode} props.buttonIcon - The icon to display on the button.
 * @param {boolean} [props.isButtonLoading=false] - Indicates whether the button is in a loading state.
 * @param {string} [props.buttonLoadingText=' '] - The text to display when the button is in a loading state.
 * @param {Object} props - The remaining props for the input field.
 * @returns {ReactNode} The rendered input component.
 */

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  type?: string;
  description?: string;
  placeholder?: string;
  label?: string;
  leftIcon?: any;
  rightIcon?: any;
  leftAction?: () => void;
  rightAction?: () => void;
  container?: string;
  inputSize?: 'default' | 'xs' | 'sm' | 'lg' | 'icon';
  hasButton?: boolean;
  buttonText?: string;
  buttonVariant?: 'default' | 'outline' | 'ghost' | 'link' | 'destructive';
  buttonAction?: () => void;
  buttonIcon?: any;
  isButtonLoading?: boolean;
  buttonLoadingText?: string;
  required?: boolean;
  /**
   * Marks the field as safe to leave empty, beside the label.
   *
   * The counterpart to `required`, and the reason it exists: "leave it blank if
   * there is no discount" was being said in a line of prose under the field,
   * which costs a whole row to carry one word and pushes the next field further
   * down every form it appears in.
   */
  optional?: boolean;
  errorText?: string;
  className?: string;
  addon?: React.ReactNode;
}

export default function Input({ id, type = 'text', value, onChange, container, description, inputSize = 'default', placeholder, label, leftIcon, rightIcon, leftAction, rightAction, hasButton = false, buttonText, buttonVariant = 'default', buttonAction, buttonIcon, isButtonLoading = false, buttonLoadingText = ' ', required = false, optional = false, errorText, className = '', addon, ...props }: InputProps) {
  const t = useT()
  const placeholderText = placeholder ?? t('common.writeHere')
  const buttonLabel = buttonText ?? t('common.save')
  // Ties the label to the field, so a screen reader still names a filled box
  // once its placeholder is gone. A caller's own id wins.
  const autoId = useId()
  const inputId = id ?? autoId

  return (
    <div className={`grid w-full items-center gap-1.5 ${container}`}>
      { label && (
        <Label htmlFor={inputId} className="mb-1">
          {label}
          { required && (<span className="text-gradient -ml-1">*</span>) }
          { optional && (<span className="ms-1.5 text-[11px] font-medium text-muted-foreground">{t('common.optional')}</span>) }
        </Label>
      ) }
      { addon ? (
        <InputGroup>
          <InputGroupAddon className="text-muted-foreground pl-3">
            {addon}
          </InputGroupAddon>
          <InputMain id={inputId} value={value} onChange={onChange} inputSize={inputSize} leftIcon={leftIcon} rightIcon={rightIcon} leftAction={leftAction} rightAction={rightAction} type={type} placeholder={placeholderText} required={required} onWheel={type == 'number' ? (e) => {e.currentTarget.blur()} : () => {}} data-slot="input-group-control" className={`flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent placeholder:text-muted-foreground -ml-1 -mt-0.5 ${className || ''}`} {...props} />
        </InputGroup>
      ) : hasButton ? (
        <div className="flex w-full items-center space-x-2">
          <InputMain id={inputId} value={value} onChange={onChange} inputSize={inputSize} leftIcon={leftIcon} rightIcon={rightIcon} leftAction={leftAction} rightAction={rightAction} type={type} placeholder={placeholderText} required={required} onWheel={type == 'number' ? (e) => {e.currentTarget.blur()} : () => {}} {...props} />
          <Button variant={buttonVariant} onClick={buttonAction} isLoading={isButtonLoading} loadingMessage={buttonLoadingText} icon={buttonIcon} size={(!buttonText && buttonIcon) ? 'icon' : 'default'} >{buttonLabel}</Button>
        </div>
      ) : (
        <InputMain id={inputId} value={value} onChange={onChange} inputSize={inputSize} leftIcon={leftIcon} rightIcon={rightIcon} leftAction={leftAction} rightAction={rightAction} type={type} placeholder={placeholderText} required={required} onWheel={type == 'number' ? (e) => {e.currentTarget.blur()} : () => {}}  className={`${className} ${errorText ? 'border-destructive!' : ''}`} {...props} />
      ) }
      { errorText ? (
        <p className="text-xs font-medium text-destructive">{errorText}</p>
      ) : description && (<p className="text-xs opacity-50 font-medium">{description}</p>)}
    </div>
  )
}